import { SMARTY_US_STREET_KEY } from '../utils/smartyConfig';
import { normalizePostalCodeValue } from '../utils/validators';
import useAddressValidationBase from './useAddressValidationBase';

const VALID_DPV_CODES = new Set(['Y', 'S', 'D']);
const CACHE_NAMESPACE = 'smarty-us-validation';
const CACHE_TTL_MS = 60 * 1000;
const RATE_LIMIT_NAMESPACE = 'smarty-us-validation';
const RATE_LIMIT_CONFIG = {
  limit: 5,
  windowMs: 60 * 1000,
  cooldownMs: 60 * 1000
};
const VALIDATION_URL = 'https://us-street.api.smarty.com/street-address';

const parseDpvFootnotes = (footnotes = '') => {
  const normalized = String(footnotes || '');
  const result = new Set();

  for (let index = 0; index < normalized.length; index += 2) {
    const code = normalized.slice(index, index + 2);
    if (code.length === 2) {
      result.add(code);
    }
  }

  return result;
};

const trimZipPlus4 = (value = '') => String(value).replace(/(\b\d{5})-\d{4}\b/g, '$1');

const buildDisplayText = (item) => {
  const line1 = item.delivery_line_1;
  const line2 = item.delivery_line_2;
  const city = item.components?.city_name;
  const region = item.components?.state_abbreviation;
  const postalCode = item.components?.zipcode;

  const lastLine = city && region ?
    [city, region, postalCode].filter(Boolean).join(' ') :
    trimZipPlus4(item.last_line || '');

  return [
    line1,
    line2,
    lastLine
  ].filter(Boolean).join(', ');
};

const buildValidationSummary = (item) => {
  const analysis = item.analysis || {};
  const dpvMatchCode = analysis.dpv_match_code || '';
  const footnotes = parseDpvFootnotes(analysis.dpv_footnotes);
  const isVacant = analysis.dpv_vacant === 'Y';
  const isNoStat = analysis.dpv_no_stat === 'Y';

  if (dpvMatchCode === 'Y' && !isVacant && !isNoStat) {
    return {
      level: 'verified',
      code: 'verified',
      title: 'Verified',
      message: 'This address is fully verified.',
      sortPriority: 1
    };
  }

  if (dpvMatchCode === 'S' && footnotes.has('CC')) {
    return {
      level: 'warning',
      code: 'invalid-secondary-optional',
      title: 'Unit not recognized',
      message: 'The main address is valid, but the unit number was not recognized.',
      sortPriority: 2
    };
  }

  if (dpvMatchCode === 'D' || footnotes.has('N1')) {
    return {
      level: 'warning',
      code: 'missing-secondary',
      title: 'Apartment or suite needed',
      message: 'The main address is valid, but a unit number is required.',
      sortPriority: 3
    };
  }

  if (dpvMatchCode === 'S' && footnotes.has('C1')) {
    return {
      level: 'warning',
      code: 'invalid-secondary-required',
      title: 'Apartment or suite not recognized',
      message: 'The street address is valid, but the unit number was not recognized and is required.',
      sortPriority: 4
    };
  }

  if ((dpvMatchCode === 'Y' || dpvMatchCode === 'S' || dpvMatchCode === 'D') && isVacant) {
    return {
      level: 'warning',
      code: 'vacant',
      title: 'Verified, but marked vacant',
      message: 'This address is valid but marked as vacant.',
      sortPriority: 5
    };
  }

  if ((dpvMatchCode === 'Y' || dpvMatchCode === 'S' || dpvMatchCode === 'D') && isNoStat) {
    return {
      level: 'warning',
      code: 'no-stat',
      title: 'Verified with caution',
      message: 'This address is valid, but USPS indicates limited delivery status.',
      sortPriority: 6
    };
  }

  return {
    level: 'invalid',
    code: 'not-confirmed',
    title: 'Not verified',
    message: 'This address could not be confirmed.',
    sortPriority: 7
  };
};

const buildValidationParams = (formData) => {
  const params = {
    key: SMARTY_US_STREET_KEY,
    street: formData.address1 || '',
    secondary: formData.address2 || '',
    city: formData.city || '',
    state: formData.region || '',
    zipcode: normalizePostalCodeValue(formData.postalCode || ''),
    candidates: 10
  };

  Object.keys(params).forEach((key) => {
    if (params[key] === '' || params[key] === null || params[key] === undefined) {
      delete params[key];
    }
  });

  return params;
};

const buildRequestKey = (formData) => JSON.stringify({
  street: formData.address1 || '',
  secondary: formData.address2 || '',
  city: formData.city || '',
  state: formData.region || '',
  zipcode: normalizePostalCodeValue(formData.postalCode || '')
});

const transformSuggestions = (responseData = []) => responseData
  .map((item) => {
    const dpvMatchCode = item.analysis?.dpv_match_code || '';

    return {
      ...item,
      dpvMatchCode,
      isDeliverable: VALID_DPV_CODES.has(dpvMatchCode),
      latitude: item.metadata?.latitude,
      longitude: item.metadata?.longitude,
      displayText: buildDisplayText(item),
      validationSummary: buildValidationSummary(item)
    };
  })
  .sort((left, right) => left.validationSummary.sortPriority - right.validationSummary.sortPriority);

const useUSAddressValidation = (formData, trigger) => {
  const isEnabled = Boolean(trigger && trigger !== 0 && formData?.address1);
  const requestParams = buildValidationParams(formData);
  const requestKey = `smarty-us-validation:${buildRequestKey(formData)}`;

  return useAddressValidationBase({
    isEnabled,
    cacheNamespace: CACHE_NAMESPACE,
    cacheTtlMs: CACHE_TTL_MS,
    rateLimitNamespace: RATE_LIMIT_NAMESPACE,
    rateLimitConfig: RATE_LIMIT_CONFIG,
    requestKey,
    requestUrl: VALIDATION_URL,
    requestParams,
    transformResponse: transformSuggestions,
    effectDeps: [trigger]
  });
};

export default useUSAddressValidation;
